import React, { useState } from 'react';
import { useI18n } from '../../hooks/useI18n';
import { resetPassword } from '../../api/authApi';

interface ResetPasswordFormProps {
  onBack: () => void;
}

export const ResetPasswordForm: React.FC<ResetPasswordFormProps> = ({ onBack }) => {
  const { t } = useI18n();
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isLoading) return;
    if (!email) {
      setError(t('login_sub_email'));
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      await resetPassword(email);
      setSuccess(true);
    } catch (err: any) {
      setError(err.response?.data?.message || t('err_reset_send') || 'Błąd podczas wysyłania linku.');
    } finally {
      setIsLoading(false);
    }
  };

  const renderStyles = () => (
    <style>{`
      .cab-reset-form * {
        box-sizing: border-box;
      }
      .cab-reset-form.cab-auth-form {
        background: #F8F6FC;
        border-radius: 32px;
        padding: 48px 32px;
        width: 100%;
        max-width: 644px;
        margin: 0 auto;
        text-align: center;
        font-family: inherit;
        margin-top: 50px;
      }
      .cab-auth-header-wrapper {
        margin-bottom: 24px;
      }
      .cab-reset-form h2 {
        font-size: 26px;
        font-weight: 800;
        color: #361777;
        margin-bottom: 12px;
      }
      .cab-sub {
        font-size: 16px;
        color: #5A4E7A;
        line-height: 1.4;
        font-weight: 500;
        margin: 0;
      }
      .cab-field {
        margin-bottom: 16px;
        position: relative;
      }
      .cab-input {
        width: 100%;
        height: 56px;
        border-radius: 100px;
        background: #FFFFFF;
        border: 1px solid #FFFFFF;
        box-shadow: 0 4px 16px rgba(45, 21, 97, 0.03);
        padding: 0 24px;
        font-size: 16px;
        color: #361777;
        font-family: inherit;
        font-weight: 500;
        transition: border-color 0.2s;
      }
      .cab-input.has-error {
        border-color: #FF5A5F;
      }
      .cab-input:focus {
        outline: none;
        border-color: #361777;
      }
      .cab-input.has-error:focus {
        border-color: #FF5A5F;
      }
      .cab-input::placeholder {
        color: #A49BB6;
        font-weight: 500;
      }
      .cab-btn-primary {
        width: 100%;
        height: 60px;
        border-radius: 100px;
        background: #361777;
        color: #fff;
        font-size: 16px;
        font-weight: 600;
        border: none;
        cursor: pointer;
        transition: opacity 0.2s;
        margin-left: auto;
        margin-right: auto;
        margin-top: 8px;
        margin-bottom: 24px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-family: inherit;
        max-width: 296px;
      }
      .cab-btn-primary:hover {
        opacity: 0.9;
      }
      .cab-btn-primary:disabled {
        opacity: 0.6;
        cursor: not-allowed;
      }
      .cab-btn-outline {
        width: 100%;
        height: 60px;
        border-radius: 100px;
        background: transparent;
        color: #361777;
        font-size: 16px;
        font-weight: 700;
        border: 1px solid #361777;
        cursor: pointer;
        transition: background 0.2s;
        display: flex;
        align-items: center;
        justify-content: center;
        margin-left: auto;
        margin-right: auto;
        max-width: 296px;
      }
      .cab-btn-outline:hover {
        background: rgba(45, 21, 97, 0.04);
      }
      .cab-error-msg {
        color: #FF5A5F;
        font-size: 14px;
        margin-bottom: 16px;
        font-weight: 500;
        text-align: center;
      }
      .cab-reset-container {
        width: 100%;
        max-width: 1360px;
        margin: 0 auto;
      }
      .cab-back-btn-wrapper {
        display: flex;
        justify-content: flex-start;
        margin-bottom: 20px;
        padding-left: 48px;
      }
      .cab-back-btn {
        background: none;
        border: none;
        padding: 0;
        cursor: pointer;
        display: flex;
        align-items: center;
        gap: 12px;
        color: #A49BB6;
        font-size: 20px;
        font-weight: 500;
        font-family: inherit;
        transition: color 0.2s;
      }
      .cab-back-btn:hover {
        color: #361777;
      }
      @media (max-width: 480px) {
        .cab-reset-form.cab-auth-form {
          padding: 24px;
          border-radius: 24px;
        }
        .cab-back-btn-wrapper {
          margin-bottom: 16px;
          justify-content: flex-start;
          padding-left: 0;
        }
        .cab-back-btn {
          font-size: 16px;
          gap: 5px;
          white-space: nowrap;
        }
      }
    `}</style>
  );

  if (success) {
    return (
      <div className="cab-reset-container">
        <div className="cab-auth-form cab-reset-form">
          <div className="cab-auth-header-wrapper">
            <h2>{t('reset_pw_title')}</h2>
            <p className="cab-sub" style={{ color: '#27AE60' }}>
              {t('reset_pw_sent')}
            </p>
          </div>
          <button className="cab-btn-outline" onClick={onBack} type="button" style={{ marginTop: '24px' }}>
            {t('powrot') || 'Powrót'}
          </button>
        </div>
        {renderStyles()}
      </div>
    );
  }

  return (
    <div className="cab-reset-container">
      <div className="cab-back-btn-wrapper">
        <button type="button" className="cab-back-btn" onClick={onBack}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6"></polyline>
          </svg>
          {t('powrot') || 'Powrót'}
        </button>
      </div>

      <div className="cab-auth-form cab-reset-form">
        <div className="cab-auth-header-wrapper">
          <h2>{t('reset_pw_title')}</h2>
          <p className="cab-sub">{t('reset_pw_sub') || 'Wpisz e-mail, aby zresetować hasło.'}</p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="cab-field">
            <input 
              type="email" 
              className={`cab-input ${error ? 'has-error' : ''}`} 
              placeholder={t('email_placeholder') || "E-mail*"}
              aria-label={t('email') || "E-mail"}
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (error) setError('');
              }}
              disabled={isLoading}
            />
          </div>

          {error && <div className="cab-error-msg">{error}</div>}

          <button 
            type="submit" 
            className="cab-btn-primary"
            disabled={isLoading}
          >
            {isLoading ? '...' : t('confirm')}
          </button>
        </form>
      </div>
      {renderStyles()}
    </div>
  );
};
